import { collection, getDocs, orderBy, query } from "firebase/firestore";
import { useEffect, useState } from "react";

import { db } from "integrations/firebase/firestore";

import { businessUnitSchema } from "./schemas";

/**
 * The company vocabulary, by name, in the same order Governance lists it.
 *
 * Read rather than hardcoded: `business_units` already has a management
 * surface in Governance and the spreadsheet import already resolves an
 * internal attendee's company against it, so a second list in the frontend
 * would be a second source of truth that drifts the first time somebody adds a
 * unit.
 *
 * A failure is swallowed on purpose. Every caller falls back to a free-text
 * company box, which is what the field was before there was a vocabulary at
 * all, so an unreachable collection costs the ordering of a dropdown rather
 * than the ability to fill the form in.
 */
export function useBusinessUnitNames(): { names: string[]; loading: boolean } {
  const [names, setNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    getDocs(query(collection(db, "business_units"), orderBy("name")))
      .then((snap) => {
        if (cancelled) return;
        const parsed: string[] = [];
        snap.forEach((unit) => {
          const result = businessUnitSchema.safeParse(unit.data());
          if (result.success) parsed.push(result.data.name);
        });
        setNames(parsed);
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return { names, loading };
}
